import { useState } from 'react';
import { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { parseMoneyToTetri } from '../lib/money';
import { formatGel, formatPoints } from '../lib/format';
import { useAdjustment, useLegacyImport } from '../lib/queries';
import { Alert, Button, Card, Field, MoneyInput, SectionTitle } from './ui';

type Mode = 'cashback' | 'points' | 'legacy';

export function LoyaltyAdjust({
  customerId, cashbackTetri, pointsBalance, lifetimeSpendTetri,
}: {
  customerId: string;
  cashbackTetri: number;
  pointsBalance: number;
  lifetimeSpendTetri: number;
}) {
  const { t } = useI18n();
  const [mode, setMode] = useState<Mode | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<string | null>(null);

  const cashback = useAdjustment(customerId, 'cashback');
  const points = useAdjustment(customerId, 'points');
  const legacy = useLegacyImport(customerId);
  const active = mode === 'points' ? points : mode === 'legacy' ? legacy : cashback;
  const errorText = useApiError();
  const error = active.error instanceof ApiError ? active.error : null;

  // Negative is meaningful here — taking cashback away is an adjustment too —
  // so the sign is read separately from the amount.
  const negative = amount.trim().startsWith('-');
  const magnitude = parseMoneyToTetri(amount);
  const tetri = negative ? -magnitude : magnitude;
  const parsed = tetri / 100;
  const valid = magnitude > 0 && reason.trim().length >= 3
    && (mode !== 'legacy' || tetri > 0);

  const reset = () => { setMode(null); setAmount(''); setReason(''); };

  const preview = () => {
    if (mode === 'cashback') {
      return `${formatGel(cashbackTetri)} → ${formatGel(cashbackTetri + tetri)}`;
    }
    if (mode === 'points') {
      return `${formatPoints(pointsBalance)} → ${formatPoints(pointsBalance + Math.round(parsed))}`;
    }
    return `${formatGel(lifetimeSpendTetri)} → ${formatGel(lifetimeSpendTetri + tetri)}`;
  };

  return (
    <Card>
      <SectionTitle>{t('adjust.title')}</SectionTitle>

      {done && <div className="mb-3"><Alert tone="success">{done}</Alert></div>}

      {!mode && (
        <>
          <p className="text-sm leading-relaxed text-ink-dim">
            {t('adjust.intro')}
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            <Button variant="ghost" size="sm" onClick={() => { setMode('cashback'); setDone(null); }}>{t('adjust.cashback')}</Button>
            <Button variant="ghost" size="sm" onClick={() => { setMode('points'); setDone(null); }}>{t('adjust.points')}</Button>
            <Button variant="ghost" size="sm" onClick={() => { setMode('legacy'); setDone(null); }}>{t('adjust.pastSpend')}</Button>
          </div>
        </>
      )}

      {mode && (
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            const onSuccess = (result: unknown) => {
              const r = result as { tierCode?: string; tierChanged?: boolean };
              setDone(
                mode === 'legacy'
                  ? t('adjust.recorded', { tier: r.tierCode, changed: r.tierChanged ? t('adjust.tierChanged') : '' })
                  : t('adjust.done'),
              );
              reset();
            };

            if (mode === 'cashback') cashback.mutate({ amount: tetri, reason: reason.trim() }, { onSuccess });
            else if (mode === 'points') points.mutate({ amount: Math.round(parsed), reason: reason.trim() }, { onSuccess });
            else legacy.mutate({ lifetimeSpendTetri: tetri, reason: reason.trim() }, { onSuccess });
          }}
        >
          {error && <Alert>{errorText(error) ?? error.message}</Alert>}

          {mode === 'legacy' && (
            <p className="text-xs leading-relaxed text-ink-faint">
              {t('adjust.pastSpendNote')}
            </p>
          )}

          {mode === 'points' ? (
            <Field
              label={t('adjust.points')}
              inputMode="numeric"
              autoFocus
              placeholder="500 or -500"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          ) : (
            <MoneyInput
              label={mode === 'legacy' ? t('adjust.pastSpend') : t('adjust.amount')}
              autoFocus
              placeholder={mode === 'legacy' ? '18500' : '30 / -30'}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          )}

          <Field
            label={t('common.reason')}
            placeholder={t('adjust.reasonPlaceholder')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            hint={t('adjust.reasonRequired')}
          />

          {valid && (
            <div className="rounded-xl border border-line bg-raised px-3 py-2.5">
              <div className="text-[0.65rem] uppercase tracking-wider text-ink-faint">{t('adjust.after')}</div>
              <div className="tabular mt-0.5 text-sm text-ink">{preview()}</div>
            </div>
          )}

          <div className="flex gap-2">
            <Button type="submit" className="flex-1" loading={active.isPending} disabled={!valid}>{t('adjust.record')}</Button>
            <Button variant="ghost" type="button" onClick={reset}>{t('common.cancel')}</Button>
          </div>
        </form>
      )}
    </Card>
  );
}
